/**
 * SEARCH POPUP — docs/13. Step 8.13.
 *
 * This file is deliberately SMALL.
 *
 * docs/47 §9 lists `component-search.js` as `<predictive-search>`. Dawn
 * already ships that element — `predictive-search.js`, kept by docs/51 §6.1 —
 * and measuring it before writing anything (the lesson of Steps 6 and 7)
 * showed it already debounces, already exposes a combobox with
 * `aria-activedescendant`, already announces its result count through a
 * `role="status"` region, and already routes ArrowUp/ArrowDown/Escape.
 * Rewriting it would be a second implementation of a working one.
 *
 * What Dawn does NOT have is our overlay: it opens predictive search from a
 * `<details>` disclosure, and docs/13 §8 lists the four things that costs.
 * Step 8.12 replaced the disclosure with our `<modal-dialog>` primitive, and
 * this file is the three lines of glue between the trigger and the dialog —
 * plus the one behaviour a dialog-hosted search field needs that a
 * disclosure-hosted one does not.
 *
 * IIFE-wrapped for the same reason as component-product-grid.js (docs/54
 * §5.6): the popup renders from the header, and the header renders on every
 * page — including ones that also render a search form.
 */
(function () {
  if (window.__dramourSearchPopupBound) return;
  window.__dramourSearchPopupBound = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-search-popup-open]');
    if (!trigger) return;

    const dialog = document.getElementById(trigger.getAttribute('aria-controls') || 'SearchPopup');
    if (!dialog || typeof dialog.show !== 'function') return;

    event.preventDefault();

    /* A TRIGGER INSIDE AN OPEN DRAWER MUST CLOSE THAT DRAWER FIRST, AND THIS
       IS NOT A NICETY — WITHOUT IT THE SEARCH OVERLAY CANNOT TAKE FOCUS.

       `<nav-drawer>` inerts every body child except the one containing
       itself while it is open, which is correct for a modal drawer and means
       `#SearchPopup` — a descendant of the header section — is `inert`. An
       inert element cannot be focused, so `dialog.show()` would open an
       overlay that swallows nothing and traps nobody, on top of a drawer
       that is still scroll-locked underneath it.

       Added 2026-08-11 with the app sheet's search row. The header's own
       trigger is not inside a drawer, so this is a no-op for it. */
    const drawer = trigger.closest('nav-drawer[open]');
    if (drawer && typeof drawer.close === 'function') drawer.close();

    trigger.setAttribute('aria-expanded', 'true');
    dialog.show(trigger);

    /**
     * ModalDialog.show sends focus to the panel — deliberately, so a screen
     * reader announces the dialog and its label before its first control
     * (see the note in global.js). For a search overlay the field is what
     * the visitor came for, so focus moves on once the announcement has
     * been made rather than instead of it.
     *
     * requestAnimationFrame, not a timeout: the panel is display:none until
     * `open` lands, and .focus() on a non-rendered element is a no-op —
     * silently, which is how the same bug survived into Step 6's quick view.
     */
    requestAnimationFrame(() => {
      const field = dialog.querySelector('input[type="search"]');
      if (field) field.focus();
    });
  });

  /**
   * The trigger's `aria-expanded` has to come back down. ModalDialog fires
   * `modalClosed` on document.body for every close path — Escape, the close
   * button, a backdrop click — so this covers all three without the popup
   * needing to know which one happened.
   */
  document.body.addEventListener('modalClosed', () => {
    document
      .querySelectorAll('[data-search-popup-open][aria-expanded="true"]')
      .forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  });

  /* ==========================================================================
     RECENT SEARCHES — 2026-08-11, docs/52 §1 S3.

     localStorage, never the server. Shopify caches this page for every
     visitor, so a server-rendered recent search would be somebody else's;
     and a search history is more sensitive than the wishlist, which docs/52
     §4's D4 already resolved the same way for the same reason.

     Capped at five. An unbounded list is the same defect class as docs/54
     §5.16's unbounded stagger — fine until the day it is not.
     ========================================================================== */
  const KEY = 'dramour:recent-searches';
  const MAX = 5;

  const readRecent = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(raw) ? raw.filter((t) => typeof t === 'string').slice(0, MAX) : [];
    } catch {
      /* A corrupt or blocked store must not take the search overlay down with
         it — Safari private mode throws on localStorage access, not just on
         write. Failing to an empty list degrades to "no recent searches",
         which is the same state a first-time visitor sees. */
      return [];
    }
  };

  const writeRecent = (term) => {
    const clean = String(term || '')
      .trim()
      .slice(0, 60);
    if (!clean) return;
    try {
      const next = [clean, ...readRecent().filter((t) => t.toLowerCase() !== clean.toLowerCase())].slice(0, MAX);
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* nothing to do — see above */
    }
  };

  const searchHref = (term) =>
    `${window.routes && window.routes.search_url ? window.routes.search_url : '/search'}` +
    `?q=${encodeURIComponent(term)}&type=product,page&options%5Bprefix%5D=last` +
    `&options%5Bunavailable_products%5D=last`;

  const renderRecent = () => {
    document.querySelectorAll('[data-search-recent]').forEach((wrap) => {
      const list = wrap.querySelector('[data-search-recent-list]');
      if (!list) return;
      const terms = readRecent();
      /* `hidden` on the WRAPPER, so the "Recent" heading cannot render over
         an empty list — docs/54 §1.-22: "A HEADING OVER NOTHING IS WORSE
         THAN NO COLUMN". */
      wrap.hidden = terms.length === 0;
      list.textContent = '';
      for (const term of terms) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'search-popup__popular-link link';
        a.href = searchHref(term);
        a.textContent = term;
        li.appendChild(a);
        list.appendChild(li);
      }
    });
  };

  /* Record on submit — the moment the visitor commits to a term, rather than
     on every keystroke, which would fill the list with prefixes of one word. */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[action*="/search"]');
    if (!form) return;
    const field = form.querySelector('input[name="q"]');
    if (field) writeRecent(field.value);
  });

  /* And when a predictive result is chosen, because that path never submits. */
  document.addEventListener('click', (event) => {
    const hit = event.target.closest('.predictive-search__item');
    if (!hit) return;
    const field = hit.closest('predictive-search, .search-popup')?.querySelector('input[name="q"]');
    if (field) writeRecent(field.value);
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-search-recent-clear]')) return;
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* see above */
    }
    renderRecent();
  });

  renderRecent();
  document.addEventListener('shopify:section:load', renderRecent);
  /* Re-render when the overlay opens, so a term added on the previous page
     view is present without a reload. */
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-search-popup-open]')) requestAnimationFrame(renderRecent);
  });
})();
